import { getYear, subYears } from 'date-fns';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { FILE_TYPES } from '_constants/fileTypes';
import { ICON_ALIASES } from '_constants/icons';
import { QUERY_PARAM_VALUE } from '_constants/queryParams';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { usePrevTaxReturnsQueryParam, usePrevious } from '_hooks';
import { selectData, selectFetchCounter, selectLoading, selectParams } from '_store/files/selector';
import { downloadFile, getFiles, refreshParams, resetFetchCounter } from '_store/files/slice';
import { selectActiveBusinessTaxFilingProductId, selectActivePersonalTaxFilingProductId } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Icon from 'components/atomic/Icon';
import Paginate from 'components/atomic/Paginate';
import Table from 'components/atomic/Table';
import Tabs from 'components/atomic/Tabs';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledBody = styled.div``;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spaces.small2X}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const StyledEmptyDataContainer = styled.div`
  padding-top: ${theme.spaces.medium}px;
`;

const StyledNavContainer = styled.div`
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledPaginateContainer = styled.div`
  padding-top: ${theme.spaces.large}px;
`;

const getColumns = ({ handleDownload }) => {
  return [
    {
      dataIndex: 'document',
      title: null,
      width: '10%',
      minWidth: '30px',
      render: () => <Icon alias={ICON_ALIASES.document} color={theme.colors.gray.secondary} />,
    },
    {
      dataIndex: 'year',
      title: 'Year',
      width: '20%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.year}
        </Text>
      ),
    },
    {
      dataIndex: 'description',
      title: 'Description',
      width: '60%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.description}
        </Text>
      ),
    },
    {
      dataIndex: 'download',
      title: null,
      width: '10%',
      minWidth: '30px',
      render: (row) => (
        <StyledIconContainer title={row?.fileName} onClick={handleDownload(row?.id, row?.fileName)}>
          <Icon alias={ICON_ALIASES.arrowDownCircle} color={theme.colors.gray.secondary} />
        </StyledIconContainer>
      ),
    },
  ];
};

const TaxReturnsView = ({ currentOnly }) => {
  const [prevTaxReturnsParamValue, setPrevTaxReturnsParamValue] = usePrevTaxReturnsQueryParam();
  const prevPrevTaxReturnsParamValue = usePrevious(prevTaxReturnsParamValue);
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const businessTaxFilingProductId = useSelector(selectActiveBusinessTaxFilingProductId);
  const personalTaxFilingProductId = useSelector(selectActivePersonalTaxFilingProductId);
  const prevBusinessTaxFilingProductId = usePrevious(businessTaxFilingProductId);
  const prevPersonalTaxFilingProductId = usePrevious(personalTaxFilingProductId);
  const params = useSelector(selectParams);
  const prevParams = usePrevious(params);
  const loading = useSelector(selectLoading);
  const data = useSelector(selectData);
  const fetchCounter = useSelector(selectFetchCounter);

  const productId =
    prevTaxReturnsParamValue === QUERY_PARAM_VALUE.business ? businessTaxFilingProductId : personalTaxFilingProductId;

  const prevProductId =
    prevTaxReturnsParamValue === QUERY_PARAM_VALUE.business
      ? prevBusinessTaxFilingProductId
      : prevPersonalTaxFilingProductId;

  const handleDownload = useCallback(
    (statementId, fileName) => () => {
      dispatch(downloadFile({ customerId, productId, statementId, fileName }));
    },
    [customerId, productId]
  );

  const handleSelectTab = (tabId) => {
    setPrevTaxReturnsParamValue(tabId);
  };

  const handlePageChange = (page) => {
    dispatch(refreshParams({ page }));
  };

  useEffect(() => {
    if (customerId && prevProductId && prevProductId === productId && !isEqual(prevParams, params)) {
      dispatch(getFiles({ customerId, productId }));
    }
  }, [customerId, productId, prevParams, params]);

  useEffect(() => {
    if (customerId && prevProductId && productId && prevProductId !== productId) {
      dispatch(getFiles({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId]);

  useEffect(() => {
    if (customerId && productId && !fetchCounter) {
      dispatch(
        refreshParams({
          count: 5,
          type: FILE_TYPES.previousYearTaxReturn,
          year: currentOnly ? subYears(new Date(), 1) : null,
        })
      );
      dispatch(getFiles({ customerId, productId }));
    }
  }, [customerId, productId, fetchCounter]);

  useEffect(() => {
    if (prevPrevTaxReturnsParamValue && prevPrevTaxReturnsParamValue !== prevTaxReturnsParamValue) {
      dispatch(resetFetchCounter());
    }
  }, [prevPrevTaxReturnsParamValue, prevTaxReturnsParamValue]);

  return (
    <Card>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600} bottomSpace={theme.spaces.large}>
            {currentOnly ? `${getYear(subYears(new Date(), 1))}` : 'Previous'} Tax returns
          </Text>
        </StyledTitleContainer>
      </StyledHeader>
      <StyledBody>
        <StyledNavContainer>
          <Tabs
            selected={prevTaxReturnsParamValue}
            items={[
              {
                tabId: QUERY_PARAM_VALUE.personal,
                iconAlias: ICON_ALIASES.profileCircle,
                title: 'Personal',
              },
              {
                tabId: QUERY_PARAM_VALUE.business,
                iconAlias: ICON_ALIASES.home,
                title: 'Business',
              },
            ]}
            onSelect={handleSelectTab}
          />
        </StyledNavContainer>
        <Table
          columns={getColumns({ handleDownload })}
          rows={data.elements}
          loading={loading}
          skeletonRowsNum={data.maxPerPage}
        />
        {!data?.totalPages && (
          <StyledEmptyDataContainer>
            <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
              No data available
            </Text>
          </StyledEmptyDataContainer>
        )}
        <StyledPaginateContainer>
          <Paginate
            pageRangeDisplayed={3}
            pageCount={data.totalPages}
            currentPage={data.page}
            onPageChange={handlePageChange}
          />
        </StyledPaginateContainer>
      </StyledBody>
    </Card>
  );
};

TaxReturnsView.propTypes = {
  currentOnly: PropTypes.bool,
};

export default TaxReturnsView;
